import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import translations from '../translations';

const NavLink = ({ link, isActive }) => (
  <Link
    to={link.path}
    data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
    className={`text-sm font-medium transition-colors duration-200 relative group ${
      isActive ? 'text-orange-500' : 'text-gray-700 hover:text-orange-500'
    }`}
  >
    {link.label}
    <span
      className={`absolute -bottom-1 left-0 h-0.5 bg-orange-500 transition-all duration-300 ${
        isActive ? 'w-full' : 'w-0 group-hover:w-full'
      }`}
    ></span>
  </Link>
);

const LangToggle = ({ lang, toggleLanguage, testId }) => (
  <button
    data-testid={testId}
    onClick={toggleLanguage}
    className="flex items-center bg-gray-100 rounded-full p-0.5 border border-gray-200 hover:border-orange-300 transition-colors duration-200"
    aria-label="Toggle language"
  >
    <span className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all duration-200 ${lang === 'en' ? 'bg-orange-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>EN</span>
    <span className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all duration-200 ${lang === 'es' ? 'bg-orange-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>ES</span>
  </button>
);

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { lang, toggleLanguage } = useLanguage();
  const t = translations[lang].nav;

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const closeMobileMenu = useCallback(() => setIsMobileMenuOpen(false), []);

  const navLinks = [
    { path: '/', label: t.home },
    { path: '/industries', label: t.industries },
    { path: '/services', label: t.services },
    { path: '/about', label: t.about },
    { path: '/contact', label: t.contact }
  ];

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center space-x-3 group">
            <img
              src="https://customer-assets.emergentagent.com/job_bimlidar-experts/artifacts/d5qki862_3D_logo_-_white_Background_200x200-removebg-preview.png"
              alt="Delta Studio Logo"
              className="h-12 w-12 transform group-hover:scale-110 transition-transform duration-300"
            />
            <span className="text-base font-bold text-gray-900">Delta Studio Scan Solutions</span>
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <NavLink key={link.path} link={link} isActive={location.pathname === link.path} />
            ))}
          </div>

          <div className="hidden md:flex items-center space-x-4">
            <LangToggle lang={lang} toggleLanguage={toggleLanguage} testId="language-toggle" />
            <Link to="/contact">
              <Button data-testid="nav-get-started-btn" className="bg-orange-500 hover:bg-orange-600 text-white transition-all duration-300 hover:shadow-lg hover:scale-105">
                {t.getStarted}
              </Button>
            </Link>
          </div>

          <div className="flex md:hidden items-center space-x-3">
            <LangToggle lang={lang} toggleLanguage={toggleLanguage} testId="language-toggle-mobile" />
            <button
              className="text-gray-900 hover:text-orange-500 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden py-4 bg-white border-t border-gray-200">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={closeMobileMenu}
                className={`block py-3 px-4 text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'text-orange-500 bg-orange-50'
                    : 'text-gray-700 hover:text-orange-500 hover:bg-gray-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="px-4 mt-4">
              <Link to="/contact" onClick={closeMobileMenu}>
                <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white">
                  {t.getStarted}
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
